import { createContext, useContext, useState, useEffect } from "react";

export const translations = {
  fr: {
    nav: {
      services: "Services",
      manifesto: "Manifeste",
      about: "À propos",
      contact: "Contact",
      quote: "Soumission",
    },
    hero: {
      badge: "01 — ENTRETIEN COMMERCIAL & INDUSTRIEL",
      line1: "Clarté.",
      line2: "Précision.",
      line3: "Industrie.",
      subtitle:
        "Nous nettoyons les immeubles commerciaux, usines agroalimentaires et sites manufacturiers avec la rigueur d'une équipe d'ingénierie.",
      cta: "Demander une soumission",
      secondary: "Voir nos services",
      stat1: "24/7",
      stat1Label: "Disponibilité opérationnelle",
      stat2: "15+",
      stat2Label: "Années d'expertise sectorielle",
      stat3: "HACCP",
      stat3Label: "Conformité agroalimentaire",
    },
    services: {
      overline: "02 — DOMAINES D'INTERVENTION",
      title: "Deux terrains. Une seule exigence.",
      subtitle:
        "Un protocole unique adapté à deux environnements exigeants. Aucune improvisation.",
      commercial: {
        tag: "COMMERCIAL",
        title: "Immeubles & Bureaux",
        desc: "Tours de bureaux, immeubles corporatifs, cliniques, centres commerciaux. Entretien récurrent et travaux spécialisés.",
        items: [
          "Nettoyage récurrent quotidien / hebdomadaire",
          "Lavage de vitres intérieures et extérieures",
          "Entretien des planchers, cirage et polissage",
          "Décontamination des aires communes",
          "Gestion des matières résiduelles",
        ],
      },
      industrial: {
        tag: "INDUSTRIEL",
        title: "Usines & Agroalimentaire",
        desc: "Usines de transformation alimentaire, sites manufacturiers, entrepôts. Conformité HACCP, MAPAQ et normes SST.",
        items: [
          "Sanitation agroalimentaire (protocoles HACCP)",
          "Nettoyage haute pression des équipements",
          "Décontamination des zones de production",
          "Entretien des planchers industriels",
          "Nettoyage post-construction et rénovation",
        ],
      },
    },
    marquee: [
      "SANITATION",
      "CONFORMITÉ",
      "HYGIÈNE",
      "PERFORMANCE",
      "HACCP",
      "PRÉCISION",
    ],
    manifesto: {
      overline: "03 — LA MÉTHODE",
      title: "Un manifeste opérationnel.",
      chapters: [
        {
          num: "01",
          title: "Diagnostic",
          body: "Chaque site est audité avant toute intervention. Nous cartographions les zones critiques, identifions les risques microbiologiques et bâtissons un plan sur mesure.",
        },
        {
          num: "02",
          title: "Protocole",
          body: "Nos équipes suivent des procédures écrites, traçables et vérifiables. Les produits utilisés sont homologués et adaptés à chaque type de surface.",
        },
        {
          num: "03",
          title: "Traçabilité",
          body: "Chaque passage est documenté. Rapports numériques, photos avant/après, registre de conformité — vous savez exactement ce qui a été fait.",
        },
        {
          num: "04",
          title: "Amélioration continue",
          body: "Nous mesurons, ajustons, formons. La qualité ne se déclare pas, elle se prouve. Un audit mensuel garantit la constance du service.",
        },
      ],
    },
    quote: {
      overline: "04 — SOUMISSION",
      title: "Obtenez votre soumission.",
      subtitle: "Répondez en 90 secondes. Nous revenons vers vous sous 24 h.",
      name: "Nom complet",
      email: "Courriel",
      phone: "Téléphone",
      company: "Entreprise (optionnel)",
      buildingType: "Type de bâtiment",
      buildingOptions: [
        { value: "commercial", label: "Immeuble commercial / bureaux" },
        { value: "agroalimentaire", label: "Usine agroalimentaire" },
        { value: "manufacturier", label: "Usine manufacturière" },
        { value: "entrepot", label: "Entrepôt / distribution" },
        { value: "autre", label: "Autre" },
      ],
      area: "Superficie approximative (pi² ou m²)",
      frequency: "Fréquence souhaitée",
      frequencyOptions: [
        { value: "quotidien", label: "Quotidien" },
        { value: "hebdomadaire", label: "Hebdomadaire" },
        { value: "bimensuel", label: "Aux 2 semaines" },
        { value: "mensuel", label: "Mensuel" },
        { value: "unique", label: "Ponctuel / une fois" },
      ],
      services: "Services requis (plusieurs choix)",
      servicesOptions: [
        "Nettoyage récurrent",
        "Sanitation HACCP",
        "Vitres",
        "Planchers",
        "Post-construction",
        "Haute pression",
        "Décontamination",
      ],
      address: "Adresse du site",
      message: "Détails supplémentaires",
      submit: "Envoyer la demande",
      submitting: "Envoi en cours…",
      success: "Demande envoyée. Nous vous contactons sous 24 h.",
      error: "Une erreur est survenue. Veuillez réessayer.",
    },
    contact: {
      overline: "05 — CONTACT",
      title: "Une question rapide?",
      subtitle: "Écrivez-nous directement.",
      name: "Nom",
      email: "Courriel",
      phone: "Téléphone (facultatif)",
      message: "Votre message",
      submit: "Envoyer",
      submitting: "Envoi…",
      success: "Message envoyé.",
      error: "Erreur. Réessayez.",
    },
    footer: {
      tagline: "Entretien commercial & industriel. Sans compromis.",
      rights: "Tous droits réservés.",
    },
  },
  en: {
    nav: {
      services: "Services",
      manifesto: "Manifesto",
      about: "About",
      contact: "Contact",
      quote: "Get a quote",
    },
    hero: {
      badge: "01 — COMMERCIAL & INDUSTRIAL CLEANING",
      line1: "Clarity.",
      line2: "Precision.",
      line3: "Industry.",
      subtitle:
        "We clean commercial buildings, food-processing plants and manufacturing sites with the rigor of an engineering crew.",
      cta: "Request a quote",
      secondary: "See our services",
      stat1: "24/7",
      stat1Label: "Operational availability",
      stat2: "15+",
      stat2Label: "Years of sector expertise",
      stat3: "HACCP",
      stat3Label: "Food-grade compliance",
    },
    services: {
      overline: "02 — WHAT WE HANDLE",
      title: "Two terrains. One standard.",
      subtitle:
        "A single protocol adapted to two demanding environments. No improvisation.",
      commercial: {
        tag: "COMMERCIAL",
        title: "Buildings & Offices",
        desc: "Office towers, corporate buildings, clinics, retail. Recurring maintenance and specialized work.",
        items: [
          "Daily / weekly recurring cleaning",
          "Interior and exterior window washing",
          "Floor maintenance, waxing and polishing",
          "Common area decontamination",
          "Waste management",
        ],
      },
      industrial: {
        tag: "INDUSTRIAL",
        title: "Plants & Food-Processing",
        desc: "Food-processing plants, manufacturing sites, warehouses. HACCP, MAPAQ and health-safety compliance.",
        items: [
          "Food-grade sanitation (HACCP protocols)",
          "High-pressure equipment cleaning",
          "Production zone decontamination",
          "Industrial floor maintenance",
          "Post-construction and renovation cleanup",
        ],
      },
    },
    marquee: [
      "SANITATION",
      "COMPLIANCE",
      "HYGIENE",
      "PERFORMANCE",
      "HACCP",
      "PRECISION",
    ],
    manifesto: {
      overline: "03 — THE METHOD",
      title: "An operational manifesto.",
      chapters: [
        {
          num: "01",
          title: "Diagnosis",
          body: "Every site is audited before any intervention. We map the critical zones, identify microbiological risks and build a tailored plan.",
        },
        {
          num: "02",
          title: "Protocol",
          body: "Our teams follow written, traceable, verifiable procedures. Products are approved and matched to every surface.",
        },
        {
          num: "03",
          title: "Traceability",
          body: "Every pass is documented. Digital reports, before/after photos, compliance logs — you know exactly what was done.",
        },
        {
          num: "04",
          title: "Continuous improvement",
          body: "We measure, adjust, train. Quality isn't declared, it's proven. A monthly audit guarantees consistency.",
        },
      ],
    },
    quote: {
      overline: "04 — QUOTE",
      title: "Get your quote.",
      subtitle: "Answer in 90 seconds. We reply within 24 hours.",
      name: "Full name",
      email: "Email",
      phone: "Phone",
      company: "Company (optional)",
      buildingType: "Building type",
      buildingOptions: [
        { value: "commercial", label: "Commercial building / offices" },
        { value: "agroalimentaire", label: "Food-processing plant" },
        { value: "manufacturier", label: "Manufacturing plant" },
        { value: "entrepot", label: "Warehouse / distribution" },
        { value: "autre", label: "Other" },
      ],
      area: "Approx. area (sqft or m²)",
      frequency: "Desired frequency",
      frequencyOptions: [
        { value: "quotidien", label: "Daily" },
        { value: "hebdomadaire", label: "Weekly" },
        { value: "bimensuel", label: "Bi-weekly" },
        { value: "mensuel", label: "Monthly" },
        { value: "unique", label: "One-time" },
      ],
      services: "Required services (multiple)",
      servicesOptions: [
        "Recurring cleaning",
        "HACCP sanitation",
        "Windows",
        "Floors",
        "Post-construction",
        "High-pressure",
        "Decontamination",
      ],
      address: "Site address",
      message: "Additional details",
      submit: "Send request",
      submitting: "Sending…",
      success: "Sent. We'll contact you within 24 h.",
      error: "Something went wrong. Please retry.",
    },
    contact: {
      overline: "05 — CONTACT",
      title: "Quick question?",
      subtitle: "Write to us directly.",
      name: "Name",
      email: "Email",
      phone: "Phone (optional)",
      message: "Your message",
      submit: "Send",
      submitting: "Sending…",
      success: "Message sent.",
      error: "Error. Try again.",
    },
    footer: {
      tagline: "Commercial & industrial cleaning. No compromise.",
      rights: "All rights reserved.",
    },
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [locale, setLocale] = useState(() => localStorage.getItem("locale") || "fr");
  useEffect(() => {
    localStorage.setItem("locale", locale);
    document.documentElement.lang = locale;
  }, [locale]);
  const t = translations[locale];
  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
